"use client"

import { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import Image from "next/image"
import {
  Volume2, Mic, Globe, Heart, ShieldOff, Clock,
  RefreshCw, Sparkles, TrendingUp,
  MessageCircle, Languages, BarChart3,
  CheckCircle, ArrowRight, Check,
  ChevronDown,
} from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import Logo from "../../components/Logo"
import WhatsAppWidget from "../../components/WhatsAppWidget"
import ScrollDepthTracker from "../../components/ScrollDepthTracker"
import GeneralRosetVsGpt from "../../components/GeneralRosetVsGpt"
import { trackEvent } from "../../lib/analytics"
import ThemeToggle from "../../components/ThemeToggle"

// ─── TopMenu ────────────────────────────────────────────────
function TopMenuEN() {
  const [isOpen, setIsOpen] = useState(false)
  const router = useRouter()
  const { user } = useAuth()

  const handleLogin = useCallback(async () => {
    if (user) { router.push("/app"); return }
    try {
      const { supabase } = await import("../../lib/supabase")
      const redirectTo = `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      })
      if (error) console.error("Google login error:", error.message)
    } catch (err) {
      console.error("Login error:", err)
    }
  }, [user, router])

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
    setIsOpen(false)
  }

  const navItems = [
    { label: "Benefits", action: () => scrollTo("benefits-section") },
    { label: "FAQ", action: () => scrollTo("faq-section") },
    { label: "Contact", action: () => { window.open("https://wa.me/+4552803868", "_blank", "noopener,noreferrer"); setIsOpen(false) } },
  ]

  return (
    <nav className="w-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200/60 dark:border-slate-700/60 h-16 flex items-center z-50 sticky top-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center w-full">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Roset</span>
        </div>
        <div className="hidden md:flex items-center gap-7">
          {navItems.map((item, i) => (
            <button key={i} onClick={item.action} className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-sm font-medium transition-colors duration-150">
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2.5">
          {!user && (
            <button onClick={() => { trackEvent("cta_click", { location: "nav" }); router.push("/app") }}
              className="hidden sm:inline-flex px-4 py-1.5 text-sm font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-500 transition duration-150">
              Start Speaking FREE
            </button>
          )}
          <ThemeToggle />
          <button onClick={handleLogin}
            className="px-4 py-1.5 text-sm font-medium rounded-full text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition duration-150">
            {user ? "Open App" : "Sign In"}
          </button>
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1">
            <span className={`w-5 h-0.5 bg-slate-600 dark:bg-slate-300 transition-all duration-200 ${isOpen ? "rotate-45 translate-y-1.5" : ""}`} />
            <span className={`w-5 h-0.5 bg-slate-600 dark:bg-slate-300 transition-all duration-200 ${isOpen ? "opacity-0" : ""}`} />
            <span className={`w-5 h-0.5 bg-slate-600 dark:bg-slate-300 transition-all duration-200 ${isOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item, i) => (
              <button key={i} onClick={item.action}
                className="block w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 font-medium py-2.5 px-3 rounded-lg text-sm transition-colors duration-150">
                {item.label}
              </button>
            ))}
            {!user && (
              <button onClick={() => { trackEvent("cta_click", { location: "nav_mobile" }); router.push("/app"); setIsOpen(false) }}
                className="block w-full text-center bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-full transition duration-150 mt-2">
                Start Speaking FREE
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

// ─── HeroSection ────────────────────────────────────────────
function HeroSectionEN() {
  const router = useRouter()
  return (
    <section className="relative bg-white dark:bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 dark:bg-blue-900/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-orange-100/30 dark:bg-orange-900/15 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
      <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-gray-900 dark:text-white tracking-tight">
              Speak a new language with confidence. Your AI tutor has infinite patience.
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.2 }}
              className="text-lg text-gray-500 dark:text-slate-400 mt-6 leading-relaxed max-w-lg">
              Have fun conversations, role-play real situations — Roset helps you handle any topic in the language you want to learn
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}
              className="mt-8 flex flex-col sm:flex-row items-start gap-4">
              <button onClick={() => { trackEvent("cta_click", { location: "hero" }); router.push("/app") }}
                className="group bg-blue-500 hover:bg-blue-600 text-white text-lg font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98]">
                <span className="flex items-center gap-2">
                  Start Speaking FREE
                  <svg className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
              <p className="text-sm text-gray-400 dark:text-slate-500 self-center">No credit card required</p>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block">
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-xl shadow-gray-200/60 dark:shadow-slate-900/60">
              <div className="bg-gradient-to-r from-blue-100 to-orange-100 dark:from-blue-900/40 dark:to-orange-900/40 px-5 py-3 flex items-center justify-between border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">AI</div>
                  <div>
                    <p className="text-gray-700 dark:text-slate-200 text-sm font-medium">Pierre</p>
                    <p className="text-gray-500 dark:text-slate-400 text-xs">French Tutor</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-gray-500 dark:text-slate-400 text-xs">Online</span>
                </div>
              </div>
              <div className="p-5 space-y-4 bg-white dark:bg-slate-800">
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-xs shrink-0 mt-0.5">AI</div>
                  <div>
                    <div className="bg-[#F7F7F8] dark:bg-slate-700 rounded-2xl rounded-tl-md px-4 py-2.5">
                      <p className="text-sm text-gray-900 dark:text-slate-100">Bonjour! Qu&apos;est-ce que tu as fait ce week-end?</p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 px-1">
                      <Volume2 className="w-3 h-3 text-gray-400 dark:text-slate-500" />
                      <div className="flex gap-px items-end">
                        {[4,8,6,10,5,9,7,11,4,8,6,3].map((h,i) => (
                          <div key={i} className="w-0.5 bg-blue-400/50 rounded-full" style={{ height: `${h}px` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-[#E8EFFF] dark:bg-blue-900/40 rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%]">
                    <p className="text-sm text-[#1D3B9D] dark:text-blue-300">Je suis alle au cinema avec mes amis.</p>
                  </div>
                </div>
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-xs shrink-0 mt-0.5">AI</div>
                  <div className="bg-[#F7F7F8] dark:bg-slate-700 rounded-2xl rounded-tl-md px-4 py-2.5">
                    <p className="text-sm text-gray-900 dark:text-slate-100">Super! Quel film avez-vous vu?</p>
                    <p className="text-xs text-blue-500 mt-1 cursor-pointer hover:text-blue-400 transition">Tap to translate</p>
                  </div>
                </div>
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
                    <Mic className="w-3 h-3" />
                  </div>
                  <div className="bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-2xl rounded-tl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                      <span className="text-xs text-gray-500 dark:text-slate-400">Listening...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ─── SocialProofBar ─────────────────────────────────────────
function SocialProofBarEN() {
  const stats = [
    { icon: <Globe className="w-4 h-4" />, value: "14", label: "Languages" },
    { icon: <Heart className="w-4 h-4" />, value: "Live", label: "Voice Conversations" },
    { icon: <ShieldOff className="w-4 h-4" />, value: "Free", label: "To Try" },
    { icon: <Clock className="w-4 h-4" />, value: "24/7", label: "To Get Started" },
  ]
  return (
    <section className="bg-slate-900 border-t border-slate-800">
      <div className="max-w-5xl mx-auto px-6 py-4">
        <div className="flex flex-wrap justify-center gap-6 md:gap-12">
          {stats.map((stat, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="text-blue-400">{stat.icon}</div>
              <span className="text-white font-semibold text-sm">{stat.value}</span>
              <span className="text-slate-500 text-sm">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── LanguageCarousel ───────────────────────────────────────
const languageListEN = [
  { flag: "\ud83c\uddfa\ud83c\uddf8", name: "English" }, { flag: "\ud83c\uddea\ud83c\uddf8", name: "Spanish" },
  { flag: "\ud83c\uddeb\ud83c\uddf7", name: "French" }, { flag: "\ud83c\udde9\ud83c\uddea", name: "German" },
  { flag: "\ud83c\uddee\ud83c\uddf9", name: "Italian" }, { flag: "\ud83c\uddf5\ud83c\uddf9", name: "Portuguese" },
  { flag: "\ud83c\uddf7\ud83c\uddfa", name: "Russian" }, { flag: "\ud83c\udde8\ud83c\uddf3", name: "Chinese" },
  { flag: "\ud83c\uddef\ud83c\uddf5", name: "Japanese" }, { flag: "\ud83c\uddf0\ud83c\uddf7", name: "Korean" },
  { flag: "\ud83c\uddf3\ud83c\uddf1", name: "Dutch" }, { flag: "\ud83c\uddf5\ud83c\uddf1", name: "Polish" },
  { flag: "\ud83c\uddfa\ud83c\udde6", name: "Ukrainian" }, { flag: "\ud83c\udde9\ud83c\uddf0", name: "Danish" },
]

function LanguageCarouselEN() {
  return (
    <section className="py-12 bg-slate-50 dark:bg-slate-900 border-y border-slate-200/60 dark:border-slate-700/60">
      <div className="max-w-5xl mx-auto px-6">
        <p className="text-center text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-6">
          Live Voice Conversations in 14 Languages:
        </p>
        <div className="flex flex-wrap justify-center gap-2.5">
          {languageListEN.map((lang, idx) => (
            <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-600 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150">
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── HowItWorks ─────────────────────────────────────────────
function HowItWorksEN() {
  const router = useRouter()
  const steps = [
    { number: "01", icon: <Globe className="w-6 h-6" />, title: "Choose Your Language", desc: "Pick from 14 languages and tell your AI tutor what you're into — music, travel, history, sports, anything. That's what you'll talk about.", accent: "from-blue-500 to-cyan-400" },
    { number: "02", icon: <Mic className="w-6 h-6" />, title: "Start a Conversation", desc: "Speak naturally using your microphone. Mess up the grammar, forget words, ask the same thing 10 times. Your AI tutor doesn't judge — it's here to help.", accent: "from-blue-600 to-indigo-500" },
    { number: "03", icon: <TrendingUp className="w-6 h-6" />, title: "Learn as You Speak", desc: "Your pronunciation improves naturally — the AI only understands you when you say it right. You build vocabulary through real conversations, not flashcards.", accent: "from-indigo-500 to-blue-400" },
  ]
  return (
    <section className="relative py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 overflow-hidden">
      {/* Subtle dot grid background */}
      <div className="absolute inset-0 opacity-[0.4] dark:opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      {/* Soft gradient orbs */}
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-blue-100/60 dark:bg-blue-900/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-indigo-100/40 dark:bg-indigo-900/20 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-16">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-3">Ready in 60 seconds</p>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">How Roset Works</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-4 max-w-lg mx-auto">Three simple steps to start speaking a new language with confidence.</p>
        </motion.div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Connecting line (desktop only) */}
          <div className="hidden md:block absolute top-[3.75rem] left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px">
            <div className="w-full h-full bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200" />
            <div className="absolute top-1/2 left-0 w-2 h-2 -translate-y-1/2 rounded-full bg-blue-300" />
            <div className="absolute top-1/2 right-0 w-2 h-2 -translate-y-1/2 rounded-full bg-blue-300" />
          </div>

          {steps.map((step, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="group relative">
              <div className="relative bg-white dark:bg-slate-800/80 rounded-2xl p-8 h-full border border-slate-200/80 dark:border-slate-700/80 shadow-sm hover:shadow-xl hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20 hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-500">
                {/* Gradient top border accent */}
                <div className={`absolute top-0 left-6 right-6 h-0.5 rounded-full bg-gradient-to-r ${step.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                {/* Step number + icon row */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`relative flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${step.accent} shadow-lg shadow-blue-200/50`}>
                    <div className="text-white">{step.icon}</div>
                  </div>
                  <span className="text-5xl font-black text-slate-100 dark:text-slate-800 group-hover:text-blue-50 dark:group-hover:text-blue-900/30 transition-colors duration-300 select-none">{step.number}</span>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 leading-snug">{step.title}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="text-center mt-14">
          <button onClick={() => { trackEvent("cta_click", { location: "how_it_works" }); router.push("/app") }}
            className="group relative inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3.5 rounded-full transition duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/30">
            Try a Free Conversation
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
          </button>
        </motion.div>
      </div>
    </section>
  )
}

// ─── BenefitsSection ────────────────────────────────────────
function BenefitsSectionEN() {
  const router = useRouter()
  const benefits = [
    {
      icon: <RefreshCw className="w-6 h-6" />, highlight: "24/7",
      title: "Your Tireless 24/7 Tutor",
      desc: "A tutor with infinite patience who knows about the topics you care about. Early morning or late at night, Roset is there for you.",
      features: ["Available 24 hours every day", "Infinite patience guaranteed", "Expert in every conversation topic"],
    },
    {
      icon: <Sparkles className="w-6 h-6" />, highlight: "Fun Learning",
      title: "Learning That Feels Like Play",
      desc: "Talk about topics you actually enjoy: movies, travel, sports, science, and more.",
      features: ["Choose your favorite topics", "Natural conversation flow", "Learning disguised as fun"],
    },
    {
      icon: <ShieldOff className="w-6 h-6" />, highlight: "Real Scenarios",
      title: "Build Real-World Confidence",
      desc: "Practice real situations like shopping, hotel reservations, or airport conversations.",
      features: ["Restaurant & shopping scenarios", "Business meeting practice", "Travel situation rehearsals"],
    },
    {
      icon: <Mic className="w-6 h-6" />, highlight: "Perfect Pronunciation",
      title: "Nail Your Pronunciation",
      desc: "The app transcribes correctly when you pronounce well. Don't worry — you can try as many times as you need.",
      features: ["Instant pronunciation feedback", "Unlimited practice attempts", "Progressive difficulty adjustment"],
    },
  ]
  return (
    <section className="py-24 bg-white dark:bg-slate-950 relative overflow-hidden">
      {/* Subtle background accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50/80 dark:bg-blue-900/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-50/60 dark:bg-indigo-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-16">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Why Roset</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Everything you need to speak fluently</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {benefits.map((b, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="group relative rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 p-7 shadow-sm hover:border-blue-200 dark:hover:border-blue-700 hover:shadow-xl hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20 transition-all duration-500">
              <div className="flex gap-5">
                <div className="shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 border border-blue-200/50 dark:border-blue-700/50 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:from-blue-100 group-hover:to-blue-150 group-hover:border-blue-300/50 transition-all duration-500 group-hover:shadow-lg group-hover:shadow-blue-200/40 dark:group-hover:shadow-blue-900/40">
                    {b.icon}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="inline-block text-[11px] font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 px-2.5 py-0.5 rounded-md">{b.highlight}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">{b.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">{b.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {b.features.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/50 border border-slate-200/80 dark:border-slate-600 rounded-lg px-2.5 py-1.5">
                        <Check className="w-3 h-3 text-blue-500" />
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-16">
          <button onClick={() => { trackEvent("cta_click", { location: "benefits" }); router.push("/app") }}
            className="group relative bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3.5 rounded-full transition duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/30">
            <span className="flex items-center gap-2">
              Start Speaking Fearlessly Today
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </span>
          </button>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-4">No credit card required &bull; Cancel anytime &bull; Start speaking in 60 seconds</p>
        </div>
      </div>
    </section>
  )
}

// ─── ProductDemo ────────────────────────────────────────────
function ProductDemoEN() {
  const features = [
    { icon: <MessageCircle className="w-5 h-5" />, title: "Live Voice Transcription", desc: "Speak naturally and watch your words appear in real time. When the AI understands you, you know your pronunciation is on track." },
    { icon: <Volume2 className="w-5 h-5" />, title: "Hear How It Should Sound", desc: "Every AI response is spoken aloud with native pronunciation. Train your ear by listening and try to match it." },
    { icon: <Languages className="w-5 h-5" />, title: "Tap to Translate Any Word", desc: "Stuck on a word? Tap it for an instant translation. You learn vocabulary in context, from real conversations." },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Your Vocabulary Grows Naturally", desc: "The more you speak, the more words you learn. Review past conversations and watch your vocabulary expand session by session." },
  ]
  return (
    <section className="py-20 bg-white dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-14">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">See it in action</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">A Real Conversation, Not a Quiz</h2>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="relative mx-auto w-full max-w-sm">
            <div className="bg-slate-900 rounded-[2rem] p-2.5 shadow-2xl shadow-slate-900/30">
              <div className="bg-white dark:bg-slate-800 rounded-[1.5rem] overflow-hidden">
                <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-semibold">Y</div>
                    <p className="text-white text-sm font-medium">Yuki - Japanese Tutor</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-blue-200 text-xs">Online</span>
                  </div>
                </div>
                <div className="p-3.5 space-y-3 min-h-[280px] bg-slate-50 dark:bg-slate-900">
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">AI</div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm max-w-[82%]">
                      <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug">Hi! Have you watched any good anime recently? I just finished one about cooking.</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Volume2 className="w-2.5 h-2.5 text-slate-400" />
                        <div className="flex gap-px items-end">
                          {[4,7,5,9,4,8,6,10,3,7].map((h,i) => <div key={i} className="w-0.5 bg-blue-400/60 rounded-full" style={{ height: `${h}px` }} />)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-blue-600 rounded-xl rounded-tr-sm px-3 py-2 max-w-[82%]">
                      <p className="text-sm text-white leading-snug">Yes! I watched Spy x Family. It was really fun.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">AI</div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm max-w-[82%]">
                      <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug">I love that show! Who&apos;s your favorite character — Anya or Yor?</p>
                      <p className="text-xs text-blue-500 mt-0.5 cursor-pointer">Tap to translate any word</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-blue-600 rounded-xl rounded-tr-sm px-3 py-2 max-w-[82%]">
                      <p className="text-sm text-white leading-snug">Anya is my favorite. She&apos;s so cute and funny!</p>
                    </div>
                  </div>
                </div>
                <div className="px-3 py-2.5 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2.5">
                  <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full px-3.5 py-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">Tap to speak or type...</span>
                  </div>
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          <div className="space-y-5">
            {features.map((f, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.3, delay: idx * 0.1 }} className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">{f.icon}</div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-0.5">{f.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}


// ─── PricingSection ─────────────────────────────────────────
function PricingSectionEN() {
  const router = useRouter()

  const freeFeatures = ["Live voice conversations", "14 languages available", "Real-time transcription", "Limited daily messages", "Basic conversation topics"]
  const premiumFeatures = ["Everything in Free, plus:", "Unlimited conversations", "All topics and scenarios", "One-click translation on any word", "Conversation history and memory", "Progress tracking and analytics", "Priority voice processing"]

  const monthlyPrice = 11.99
  const yearlyPrice = 59
  const yearlyMonthly = Math.round((yearlyPrice / 12) * 100) / 100
  const savingsPercent = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100)

  return (
    <section className="py-20 bg-white dark:bg-slate-950">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-10">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">Pricing</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Start Free, No Strings Attached</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-3 max-w-lg mx-auto">Have your first conversation today. Upgrade when you want unlimited practice.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
            className="border border-slate-200 dark:border-slate-700 rounded-xl p-7">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Free</h3>
            <div className="mt-3 mb-5"><span className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight">$0</span></div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">Try real conversations and feel what it&apos;s like to practice without pressure.</p>
            <button onClick={() => { trackEvent("cta_click", { location: "pricing_free" }); router.push("/app") }}
              className="w-full py-2.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition duration-150">
              Start Free
            </button>
            <ul className="mt-6 space-y-2.5">
              {freeFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.1 }}
            className="border border-slate-200 dark:border-slate-700 rounded-xl p-7">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Monthly</h3>
            <div className="mt-3 mb-1">
              <span className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight">${monthlyPrice}</span>
              <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">/mo</span>
            </div>
            <div className="mb-5" />
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">Unlimited conversations for those who want to practice every day.</p>
            <button onClick={() => { trackEvent("cta_click", { location: "pricing_monthly" }); router.push("/app") }}
              className="w-full py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition duration-150">
              Start Free
            </button>
            <ul className="mt-6 space-y-2.5">
              {premiumFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 }}
            className="border-2 border-blue-600 rounded-xl p-7 relative bg-blue-50/30 dark:bg-blue-900/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-0.5 rounded-full">Most Popular</div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Yearly</h3>
            <div className="mt-3 mb-1">
              <span className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight">${yearlyMonthly}</span>
              <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">/mo</span>
            </div>
            <p className="text-xs text-blue-600 font-medium mb-4">Billed ${yearlyPrice}/year · Save {savingsPercent}%</p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">Unlimited conversations for those who want to practice every day.</p>
            <button onClick={() => { trackEvent("cta_click", { location: "pricing_yearly" }); router.push("/app") }}
              className="w-full py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition duration-150">
              Start Free
            </button>
            <ul className="mt-6 space-y-2.5">
              {premiumFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  )
}


// ─── FaqSection ─────────────────────────────────────────────
const faqsEN = [
  { question: "What languages does Roset support for live voice conversations?", answer: "Roset supports live voice conversations with real-time transcription in 14 languages: English, Spanish, French, German, Italian, Portuguese, Russian, Chinese, Japanese, Korean, Dutch, Polish, Ukrainian, and Danish. You can hold natural conversations with your AI tutor using your voice in any of these languages." },
  { question: "How does Roset's live transcription work?", answer: "Roset uses advanced Web Speech API technology for instant speech-to-text conversion. Simply speak naturally and watch your words appear in real time as you talk. Our AI tutor responds immediately, creating a natural conversational flow. The system works directly in your browser with no additional downloads required." },
  { question: "What makes Roset different from other language learning apps?", answer: "Unlike traditional language apps, Roset offers real-time voice conversations with AI tutors that adapt to your learning style, goals, and pace. You can speak naturally and get instant feedback, practice pronunciation, and hold meaningful conversations. Our AI personalizes lessons based on your interests and learning progress." },
  { question: "Can I learn multiple languages at the same time?", answer: "Yes! You can switch between any of our supported languages and practice with native-specialized AI tutors for each language. Each tutor adapts to your proficiency level in that specific language, letting you maintain progress across multiple languages simultaneously." },
  { question: "What devices and browsers support Roset's voice features?", answer: "Roset's live voice transcription works on both desktop and mobile devices. For the best experience, use Chrome on Android or Safari on iPhone/iPad. The platform also works on Chrome, Edge, and Safari desktop browsers. An internet connection is required for live transcription." },
  { question: "Is Roset suitable for complete beginners?", answer: "Absolutely! Roset is designed to guide you from your very first words. Our AI tutors start with basics like greetings and common phrases, then gradually introduce more complex concepts. The adaptive learning system ensures you're never overwhelmed and always challenged at the right level." },
  { question: "How much does Roset cost?", answer: "Roset offers a free trial so you can experience learning through live conversations before committing. After your trial, we offer flexible premium plans with unlimited conversations, advanced features, and access to specialized tutors. Check our pricing section for current rates and special offers." },
  { question: "Can I practice specific topics or situations?", answer: "Yes! Our AI tutors can tailor conversations to focus on specific topics like business meetings, travel scenarios, casual chat, or academic discussions. Simply tell your tutor what you'd like to practice, and they'll create relevant, engaging conversations tailored to your interests." },
  { question: "Why should I choose Roset over ChatGPT for language learning?", answer: "comparison" },
]

function FaqSectionEN() {
  const [openIndex, setOpenIndex] = useState(null)
  const router = useRouter()

  return (
    <section className="py-20 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-12">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">Everything you need to know</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Frequently Asked Questions</h2>
        </motion.div>
        <div className="space-y-3">
          {faqsEN.map((faq, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-20px" }} transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex justify-between items-center p-5 text-left hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors duration-150">
                <span className="text-sm font-medium text-slate-900 dark:text-white pr-4">{faq.question}</span>
                <motion.div animate={{ rotate: openIndex === index ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0" />
                </motion.div>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2, ease: "easeInOut" }} className="overflow-hidden">
                    <div className="px-5 pb-5">
                      {faq.answer === "comparison" ? (
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg -mx-1 p-0"><GeneralRosetVsGpt /></div>
                      ) : (
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{faq.answer}</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <button onClick={() => { trackEvent("cta_click", { location: "faq" }); router.push("/app") }}
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3 rounded-full transition duration-200 hover:shadow-lg hover:shadow-blue-600/25">
            Still have questions?
          </button>
        </div>
      </div>
    </section>
  )
}

// ─── FinalCTA ───────────────────────────────────────────────
function FinalCTAEN() {
  const router = useRouter()
  return (
    <section className="py-16 bg-slate-950">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Ready to start speaking?</h2>
        <p className="text-slate-400 mt-3 max-w-md mx-auto">Join learners practicing real conversations in 14 languages. Free to start, no credit card required.</p>
        <button onClick={() => { trackEvent("cta_click", { location: "final_cta" }); router.push("/app") }}
          className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-lg px-8 py-4 rounded-full mt-8 transition duration-200 hover:shadow-lg hover:shadow-blue-600/25">
          Start Speaking Free
          <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-200" />
        </button>
      </motion.div>
    </section>
  )
}

// ─── Footer ─────────────────────────────────────────────────
function FooterEN() {
  return (
    <footer className="bg-slate-950 text-slate-400">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <Logo />
              <span className="text-lg font-semibold text-white">Roset</span>
            </div>
            <p className="text-sm leading-relaxed max-w-xs">Practice languages with AI-powered voice conversations. 14 languages, available 24/7.</p>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-4">Product</h4>
            <ul className="space-y-2.5">
              <li><a href="/app" className="text-sm hover:text-white transition-colors duration-150">Start Learning</a></li>
              <li><a href="/contact" className="text-sm hover:text-white transition-colors duration-150">Contact Roset</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-2.5">
              <li><a href="/privacypolicy" className="text-sm hover:text-white transition-colors duration-150">Terms & Privacy Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} Roset, Inc.</p>
          <a href="https://wa.me/+4552803868" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-white transition-colors duration-150">WhatsApp</a>
        </div>
      </div>
    </footer>
  )
}

// ─── Main Page ──────────────────────────────────────────────
export default function LandingPageEN() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col overflow-x-hidden">
      <ScrollDepthTracker />
      <WhatsAppWidget className="fixed bottom-6 right-6 z-50" />
      <TopMenuEN />
      <HeroSectionEN />
      <SocialProofBarEN />
      <LanguageCarouselEN />
      <div id="how-it-works-section"><HowItWorksEN /></div>
      <div id="benefits-section"><BenefitsSectionEN /></div>
      <ProductDemoEN />
      <div id="pricing-section"><PricingSectionEN /></div>
      <div id="faq-section"><FaqSectionEN /></div>
      <FinalCTAEN />
      <FooterEN />
    </div>
  )
}
